import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  formatCurrency,
  formatPercent,
  formatSignedCurrency,
} from '@shared/SharedAccountBody';
import { PerformanceData } from '@shared/SharedAccountBody';
import {
  PerformanceHistoryRange,
  PerformanceHistoryResponse,
  RetailAccountContent,
} from '@shared/types';
import { COLORS } from '../../constants/theme';
import {
  formatPctPill,
  formatPctPoints,
  normalizePercent,
  computePositionTotals,
  computeDailyAlpha,
  pctChangeSince,
} from './portfolioUtils';
import { StatCard, StatLabel } from '@/components/nq/StatCard';
import { Metric } from '@/components/nq/Metric';
import { PLBadge } from '@/components/nq/PLBadge';
import { DataCard } from '@/components/nq/DataCard';
import { TrendingUp } from 'lucide-react';

interface PortfolioHeroChartProps {
  content?: RetailAccountContent;
  performance?: PerformanceData;
  loading?: boolean;
  history?: PerformanceHistoryResponse;
  historyLoading?: boolean;
  historyError?: string | null;
  selectedRange: PerformanceHistoryRange;
  onRangeChange: (range: PerformanceHistoryRange) => void;
}

const RANGE_OPTIONS: PerformanceHistoryRange[] = ['1D', '1W', '1M', 'ALL'];

// Rich performance tooltip — 3-column grid (series · value · % since start) with
// the portfolio row emphasised, then "vs SP500" (gold) + "Daily delta" rows.
const TT_ROW =
  'grid grid-cols-[minmax(0,1fr)_auto_64px] items-center gap-3 whitespace-nowrap py-0.5';
const deltaTone = (pct: number | undefined) =>
  pct === undefined
    ? 'text-muted-foreground'
    : pct >= 0
      ? 'text-[color:var(--nq-up-soft)]'
      : 'text-[color:var(--nq-down-bright)]';

const PortfolioHeroChart = ({
  content,
  performance,
  loading,
  history,
  historyLoading,
  historyError,
  selectedRange,
  onRangeChange,
}: PortfolioHeroChartProps) => {
  const curveData = useMemo(
    () =>
      (history?.points ?? []).map((point, index, points) => {
        const prev = index > 0 ? points[index - 1] : undefined;
        const portfolioDayAmount =
          prev !== undefined ? point.portfolio - prev.portfolio : undefined;
        const portfolioDayPct =
          prev !== undefined
            ? pctChangeSince(point.portfolio, prev.portfolio)
            : undefined;

        return {
          dateLabel: point.label,
          portfolio: point.portfolio,
          benchmark: point.benchmark,
          portfolioDayAmount,
          portfolioDayPct,
        };
      }),
    [history?.points],
  );

  const showBenchmark = history?.benchmarkAvailable === true;

  const yDomain = useMemo((): [number, number] => {
    if (curveData.length === 0) return [0, 100];
    const vals = curveData.flatMap((d) =>
      showBenchmark ? [d.portfolio, d.benchmark] : [d.portfolio],
    );
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.08 || max * 0.05;
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [curveData, showBenchmark]);

  const rangeStart = curveData[0];

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    const portfolioEntry = payload.find(
      (entry: any) => entry.dataKey === 'portfolio',
    );
    const benchmarkEntry = payload.find(
      (entry: any) => entry.dataKey === 'benchmark',
    );
    const portfolioValue = Number(portfolioEntry?.value);
    const benchmarkValue = Number(benchmarkEntry?.value);
    const showDelta =
      showBenchmark &&
      Number.isFinite(portfolioValue) &&
      Number.isFinite(benchmarkValue) &&
      benchmarkValue > 0;
    const deltaPct = showDelta
      ? ((portfolioValue - benchmarkValue) / benchmarkValue) * 100
      : undefined;
    const deltaAmount = showDelta ? portfolioValue - benchmarkValue : undefined;

    const startValueForEntry = (dataKey: string): number | undefined => {
      if (!rangeStart) return undefined;
      if (dataKey === 'portfolio') return rangeStart.portfolio;
      if (dataKey === 'benchmark') return rangeStart.benchmark;
      return undefined;
    };

    const datum = payload[0]?.payload;
    const portfolioDayAmount: number | undefined = datum?.portfolioDayAmount;
    const portfolioDayPct: number | undefined = datum?.portfolioDayPct;

    return (
      <div className="pointer-events-none rounded-lg border border-border bg-[color:var(--nq-bg-deep)]/98 px-3 py-2 text-[13px] shadow-[var(--shadow-popover)]">
        <p className="mb-1.5 text-xs text-muted-foreground">{label}</p>
        {payload.map((entry: any) => {
          const value = Number(entry.value);
          const startValue = startValueForEntry(entry.dataKey);
          const linePct =
            startValue !== undefined
              ? pctChangeSince(value, startValue)
              : undefined;

          return (
            <div
              key={entry.dataKey}
              className={`${TT_ROW} ${
                entry.dataKey === 'portfolio' ? 'font-semibold' : ''
              }`}
            >
              <span style={{ color: entry.color }}>{entry.name}</span>
              <span className="text-right tabular-nums text-foreground">
                {formatCurrency(value)}
              </span>
              <span className={`text-right tabular-nums ${deltaTone(linePct)}`}>
                {linePct !== undefined ? formatPctPoints(linePct) : '—'}
              </span>
            </div>
          );
        })}
        {showDelta && deltaPct !== undefined && (
          <div
            className={`${TT_ROW} mt-1.5 border-t border-[color:rgba(255,255,255,0.12)] pt-1.5 text-xs font-semibold`}
          >
            <span className="text-foreground">vs SP500</span>
            <span className="text-right tabular-nums text-secondary">
              {deltaAmount !== undefined
                ? formatSignedCurrency(deltaAmount)
                : '—'}
            </span>
            <span className={`text-right tabular-nums ${deltaTone(deltaPct)}`}>
              {formatPctPoints(deltaPct)}
            </span>
          </div>
        )}
        <div
          className={`${TT_ROW} mt-1 border-t border-[color:rgba(255,255,255,0.06)] pt-1 text-xs text-muted-foreground`}
        >
          <span>Daily delta</span>
          <span
            className={`text-right tabular-nums ${deltaTone(portfolioDayAmount)}`}
          >
            {portfolioDayAmount !== undefined
              ? formatSignedCurrency(portfolioDayAmount)
              : '—'}
          </span>
          <span
            className={`text-right tabular-nums ${deltaTone(portfolioDayPct)}`}
          >
            {portfolioDayPct !== undefined
              ? formatPctPoints(portfolioDayPct)
              : '—'}
          </span>
        </div>
      </div>
    );
  };

  const sinceStartPct = performance?.sinceStart?.percent;
  // Canonical Excess Alfa from the backend (portfolio return − SPY return over the
  // anchored window). The backend realigns sinceStart.alfa to this same value, so
  // both branches below now resolve to one number — consistent with the backoffice
  // card and the chart tooltip.
  const alphaPct = content?.vsBenchmarkSinceStart?.benchmarkAvailable
    ? content.vsBenchmarkSinceStart.relativePct
    : performance?.sinceStart?.alfa;
  const todayPct = performance?.today?.percent;
  // The TradeEngine doesn't fill today.benchmarkReturn, so derive the day's SP500
  // return from the last two points of the benchmark series the chart already plots.
  const todayBenchmarkReturn = useMemo(() => {
    if (performance?.today?.benchmarkReturn !== undefined) {
      return performance.today.benchmarkReturn;
    }
    const points = history?.points ?? [];
    if (!showBenchmark || points.length < 2) return undefined;
    const last = points[points.length - 1]?.benchmark;
    const prev = points[points.length - 2]?.benchmark;
    if (!Number.isFinite(last) || !Number.isFinite(prev) || prev <= 0)
      return undefined;
    return last / prev - 1;
  }, [performance?.today?.benchmarkReturn, history?.points, showBenchmark]);

  // Daily excess alpha: today's portfolio return minus today's SP500 return.
  const todayAlpha = computeDailyAlpha(performance, todayBenchmarkReturn);
  const todayAlphaUp = todayAlpha !== undefined && todayAlpha >= 0;

  const alphaValue =
    alphaPct !== undefined ? normalizePercent(alphaPct) : undefined;
  const alphaPositive = alphaValue !== undefined && alphaValue > 0;
  const alphaStrong = alphaPositive && alphaValue >= 10;
  const alphaModerate = alphaPositive && alphaValue >= 5 && alphaValue < 10;
  const alphaNegative = alphaValue !== undefined && !alphaPositive;

  const { positionCount, totalUnrealizedPL, totalUnrealizedPLPct } = useMemo(
    () => computePositionTotals(content),
    [content],
  );
  const totalPlUp = totalUnrealizedPL >= 0;
  const sinceStartUp =
    sinceStartPct !== undefined && normalizePercent(sinceStartPct) >= 0;
  const todayAmountUp = performance?.today
    ? performance.today.amount >= 0
    : true;

  const chartLoading = (loading || historyLoading) && curveData.length === 0;

  return (
    <section className="mb-6">
      <div className="mb-4 grid grid-cols-1 gap-4 min-[900px]:grid-cols-[1.35fr_1fr_1.1fr]">
        {/* Primary overview */}
        <StatCard tone="primary" className="flex flex-col gap-3">
          <StatLabel>Portfolio overview</StatLabel>
          <div className="flex flex-wrap items-center gap-x-4.5 gap-y-3.5">
            <Metric size="xl">
              {content?.totalValue !== undefined
                ? formatCurrency(content.totalValue)
                : '—'}
            </Metric>
            {sinceStartPct !== undefined && (
              <div
                className={`flex min-w-[88px] flex-col gap-0.5 rounded-[10px] border px-3.5 py-2 ${
                  sinceStartUp
                    ? 'border-[color:var(--nq-return-up-border)] bg-[image:var(--nq-grad-return-up)] shadow-[0_0_16px_rgba(46,230,107,0.12)]'
                    : 'border-negative/35 bg-[image:linear-gradient(145deg,rgba(255,77,94,0.15),rgba(255,77,94,0.04))]'
                }`}
              >
                <Metric
                  size="lg"
                  tone={sinceStartUp ? 'up' : 'down'}
                  glow={sinceStartUp}
                >
                  {formatPctPill(sinceStartPct)}
                </Metric>
                <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  since start
                </span>
              </div>
            )}
          </div>
          <div className="mt-1 border-t border-[color:rgba(255,255,255,0.06)] pt-1.5">
            <StatLabel>Unrealized P/L</StatLabel>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Metric size="md" tone={totalPlUp ? 'up' : 'down'}>
                {formatSignedCurrency(totalUnrealizedPL)}
              </Metric>
              {totalUnrealizedPLPct !== undefined && (
                <PLBadge value={totalPlUp ? 1 : -1}>
                  {formatPercent(totalUnrealizedPLPct)}
                </PLBadge>
              )}
            </div>
          </div>
        </StatCard>

        {/* Alpha — prestige glow when outperforming */}
        <StatCard
          tone={alphaPositive ? 'glow' : 'default'}
          className={`flex flex-col items-start gap-2.5 ${
            alphaNegative
              ? 'border-negative/35 bg-[image:linear-gradient(145deg,rgba(255,77,94,0.12),rgba(255,255,255,0.02))]'
              : ''
          }`}
        >
          <StatLabel tone={alphaPositive ? 'up' : 'muted'}>
            Alpha vs SP500
          </StatLabel>
          <Metric
            size={alphaPositive ? 'hero' : 'lg'}
            tone={alphaPositive ? 'up' : alphaNegative ? 'down' : 'default'}
            glow={alphaPositive}
          >
            {alphaPct !== undefined ? formatPctPill(alphaPct) : '—'}
          </Metric>
          {(alphaStrong || alphaModerate) && (
            <span className="mt-0.5 inline-flex items-center gap-1.5 rounded-full border border-[color:var(--nq-return-up-border)] bg-positive/20 py-1 pl-2 pr-3 shadow-[0_0_20px_rgba(46,230,107,0.4),inset_0_0_12px_rgba(46,230,107,0.18)]">
              <TrendingUp
                className="size-[18px] shrink-0 text-[color:var(--nq-up-bright)]"
                strokeWidth={2.5}
                aria-hidden
              />
              <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[color:var(--nq-up-bright)]">
                {alphaStrong ? 'Outperforming S&P 500' : 'Beating benchmark'}
              </span>
            </span>
          )}
        </StatCard>

        {/* Capital — invested + today */}
        <StatCard className="flex items-stretch gap-4">
          <div className="flex flex-1 flex-col gap-1">
            <StatLabel>Invested</StatLabel>
            <Metric size="md">
              {content?.stockValue !== undefined
                ? formatCurrency(content.stockValue)
                : '—'}
            </Metric>
            <div className="mt-1 flex items-baseline justify-between border-t border-[color:rgba(255,255,255,0.06)] pt-1.5">
              <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                Cash
              </span>
              <span className="text-[13px] tabular-nums text-foreground/75">
                {content?.cash !== undefined
                  ? formatCurrency(content.cash)
                  : '—'}
              </span>
            </div>
            <div className="flex items-baseline justify-between border-t border-[color:rgba(255,255,255,0.06)] pt-1.5">
              <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                Positions
              </span>
              <span className="text-[13px] tabular-nums text-foreground/75">
                {positionCount}
              </span>
            </div>
          </div>
          <div className="w-px bg-[color:rgba(255,255,255,0.1)]" aria-hidden />
          <div className="flex flex-1 flex-col gap-1">
            <StatLabel>Today&apos;s results</StatLabel>
            <div className="flex flex-wrap items-baseline gap-2">
              <Metric
                size="md"
                tone={
                  performance?.today
                    ? todayAmountUp
                      ? 'up'
                      : 'down'
                    : 'default'
                }
              >
                {performance?.today
                  ? formatSignedCurrency(performance.today.amount)
                  : '—'}
              </Metric>
              {todayPct !== undefined && (
                <PLBadge value={normalizePercent(todayPct) >= 0 ? 1 : -1}>
                  {formatPctPill(todayPct)}
                </PLBadge>
              )}
            </div>
            <div className="mt-1 flex items-baseline justify-between border-t border-[color:rgba(255,255,255,0.06)] pt-1.5">
              <span className="text-[11px] uppercase tracking-[0.04em] text-muted-foreground">
                Alpha vs S&amp;P 500
              </span>
              <span
                className={`text-[13px] tabular-nums ${
                  !content?.marketOpen || todayAlpha === undefined
                    ? 'text-foreground/75'
                    : todayAlphaUp
                      ? 'text-[color:var(--nq-up-soft)]'
                      : 'text-[color:var(--nq-down-bright)]'
                }`}
              >
                {content?.marketOpen && todayAlpha !== undefined
                  ? formatPctPill(todayAlpha)
                  : '—'}
              </span>
            </div>
            {/* When the NYSE regular session is closed the value is the last
                completed session's result, frozen (not a live figure). */}
            {content?.marketOpen === false && (
              <span className="mt-auto pt-1 text-[10px] font-medium uppercase tracking-[0.05em] text-muted-foreground">
                (market closed)
              </span>
            )}
          </div>
        </StatCard>
      </div>

      <DataCard
        className="rounded-[14px] bg-[color:var(--nq-surface)] p-5 shadow-[var(--shadow-card)]"
        title="Performance"
        action={
          <div className="flex gap-1">
            {RANGE_OPTIONS.map((range) => (
              <button
                key={range}
                type="button"
                className={`cursor-pointer appearance-none rounded-md border border-solid px-2.5 py-1 text-xs transition-colors ${
                  selectedRange === range
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border bg-transparent text-muted-foreground hover:border-[color:rgba(255,255,255,0.15)] hover:text-foreground/65'
                }`}
                onClick={() => onRangeChange(range)}
                aria-pressed={selectedRange === range}
              >
                {range}
              </button>
            ))}
          </div>
        }
      >
        {historyError && (
          <div className="mb-2 rounded-lg border border-[color:rgba(220,80,80,0.25)] bg-[color:rgba(220,80,80,0.12)] px-3 py-2 text-[13px] text-[color:var(--nq-down-soft)]">
            {historyError}
          </div>
        )}

        {chartLoading ? (
          <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
            Loading chart…
          </div>
        ) : curveData.length === 0 ? (
          <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
            {historyError
              ? 'Performance history is unavailable for this range.'
              : 'Chart data will appear once performance history loads.'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <AreaChart
              data={curveData}
              margin={{ top: 4, right: 12, left: 8, bottom: 0 }}
            >
              <defs>
                <linearGradient id="portfolioFill" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={COLORS.portfolioGreen}
                    stopOpacity={0.45}
                  />
                  <stop
                    offset="100%"
                    stopColor={COLORS.portfolioGreen}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                stroke="rgba(255,255,255,0.05)"
                strokeDasharray="4 4"
              />
              <XAxis
                dataKey="dateLabel"
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={yDomain}
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) =>
                  v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`
                }
                width={56}
              />
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 20, outline: 'none' }}
                contentStyle={{
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  padding: 0,
                  margin: 0,
                }}
                itemStyle={{ display: 'none' }}
                labelStyle={{ display: 'none' }}
              />
              <Legend
                wrapperStyle={{
                  paddingBottom: 10,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                }}
              />
              <Area
                type="monotone"
                dataKey="portfolio"
                name="NovaQuantis Portfolio"
                stroke={COLORS.portfolioGreen}
                strokeWidth={2}
                fill="url(#portfolioFill)"
                isAnimationActive={false}
              />
              {showBenchmark && (
                <Area
                  type="monotone"
                  dataKey="benchmark"
                  name="S&P 500"
                  stroke={COLORS.benchmarkLine}
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  fill="none"
                  isAnimationActive={false}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </DataCard>
    </section>
  );
};

export default PortfolioHeroChart;
